import { EnvironmentSettingsPanel } from './panels/EnvironmentSettingsPanel';

interface OpenTokDevice {
  deviceId: string;
  label: string;
  kind?: string;
}
export interface OpenTokDeviceList {
  inputs: OpenTokInputDevice[];
  outputs: OpenTokOutputDevice[];
}
interface OpenTokInputDevice extends OpenTokDevice {
  kind: 'videoinput' | 'audioinput' | 'audiooutput';
}
interface OpenTokOutputDevice extends OpenTokDevice {}
export interface OpenTokChosenDevices {
  audioDeviceId: string | null;
  outputDeviceId: string | null;
  videoDeviceId: string | null;
}

export default class DeviceManager {
  private _devicePanel: EnvironmentSettingsPanel | null = null;
  private _deviceList: OpenTokDeviceList = { inputs: [], outputs: [] };
  private deviceChangeTimestamp: number = 0;
  private _windowStream: any;

  constructor() {
    this._enumerateDevices()
      .then(deviceList => {
        this._deviceList = deviceList;
      })
      .catch(error => {
        console.error(error);
      });
    this.setupListeners();
  }

  private _showDevicePanel(aConstructoParams): void {
    this._devicePanel = new EnvironmentSettingsPanel();
    let panelParams = {
      chosenAudio: this.getAudioDeviceId(),
      chosenVideo: this.getVideoDeviceId(),
      chosenOutput: this.getOutputDeviceId(),
      deviceList: this._deviceList,
      chosenTab: aConstructoParams?.chosenTab != null ? aConstructoParams.chosenTab : 'video_tab',
      onSave: (aDevices: OpenTokChosenDevices) => {
        if (aDevices.audioDeviceId) {
          this.setDefaultAudioDevice(aDevices.audioDeviceId);
        }
        if (aDevices.videoDeviceId) {
          this.setDefaultVideoDevice(aDevices.videoDeviceId);
        }
        if (aDevices.outputDeviceId) {
          this.setDefaultOutputDevice(aDevices.outputDeviceId);
          // this._attachSinkId(aDevices.outputDeviceId);
          OpentokSession.INSTANCE.getStreamManager()
            .getStreamContainersList()
            .forEach(function (stream) {
              stream.attachSink();
            });
        }
        App.INSTANCE.dispatch('inputdevice:switch', aDevices);
        // we call this here to reinit the default options with any chosen devices regardless of what page we are on
        this.getAllDevices().then(deviceList => {
          OpentokSession.INSTANCE.getStreamManager().setStreamOptions([]);
          if (typeof aConstructoParams.onSave === 'function') {
            App.INSTANCE.mMessageController.hide(this._devicePanel);
            aConstructoParams.onSave();
            // this._devicePanel = null;
          }
        });

        App.INSTANCE.showTextMessage('Saved!');
      },
      onRunTest: () => {
        return this._runConnectivityTest();
      },
      onSuccess: () => {
        if (typeof aConstructoParams.onSuccess == 'function') {
          aConstructoParams.onSuccess();
        }
      },
      onFailure: () => {
        if (typeof aConstructoParams.onFailure == 'function') {
          aConstructoParams.onFailure();
        }
      },
      onHide: () => {
        if (typeof aConstructoParams.onHide === 'function') {
          aConstructoParams.onHide();
        }
        App.INSTANCE.mMessageController.hide(this._devicePanel);
        this._devicePanel = null;
      },
    };
    this._devicePanel.initialize(panelParams);
    App.INSTANCE.mMessageController.show(this._devicePanel, UITransitions.slideUp);
  }

  public showDeviceChangePanel(aConstructoParams): void {
    if (this._devicePanel || App.CORDOVA) {
      return;
    }
    this._enumerateDevices()
      .then(deviceList => {
        let canResolve = true;
        deviceList.inputs.forEach(device => {
          if (device.deviceId == '') {
            canResolve = false;
          }
        });

        if (!canResolve) {
          navigator.mediaDevices
            .getUserMedia({ audio: true, video: true })
            .then(stream => {
              window.stream = stream; // make the stream available
              this._windowStream = stream;
              // have to call stop on each MediaStreamTrack to turn off any found devices
              stream.getTracks().forEach(track => {
                track.stop();
              });
              return this._enumerateDevices().then(bDeviceList => {
                this._deviceList = bDeviceList;
                this._showDevicePanel(aConstructoParams);
              });
            })
            .catch(function (err) {
              /* handle the error */
              console.warn(err);
            });
        } else {
          this._deviceList = deviceList;
          this._showDevicePanel(aConstructoParams);
        }
      })
      .catch(error => {
        console.error(error);
        aConstructoParams.chosenTab = 'connectivity_tab';
        this._showDevicePanel(aConstructoParams);
      });
  }

  destroy(): void {
    navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeDetected);
  }

  setupListeners(): void {
    navigator.mediaDevices?.addEventListener('devicechange', this.deviceChangeDetected.bind(this));
  }

  getDeviceList(): OpenTokDeviceList {
    return this._deviceList;
  }

  hasDevicePanel(): boolean {
    return this._devicePanel != null;
  }

  getAudioDeviceId(): string {
    let defaultAudioSource = localStorage.getItem('chosenAudioInputDeviceId');
    if (defaultAudioSource && this._validateDeviceId(defaultAudioSource)) {
      return defaultAudioSource;
    } else {
      // We don't have a valid saved device - use the first available
      let deviceId = this.getAvailableAudioSources()[0]?.deviceId;
      return deviceId;
    }
  }

  getVideoDeviceId(): string {
    let defaultVideoSource = localStorage.getItem('chosenVideoInputDeviceId');
    if (defaultVideoSource && this._validateDeviceId(defaultVideoSource)) {
      return defaultVideoSource;
    } else {
      // We don't have a valid saved device - use the first available
      let deviceId = this.getAvailableVideoSources()[0]?.deviceId;
      return deviceId;
    }
  }

  getOutputDeviceId(): string {
    let defaultOutputSource = localStorage.getItem('chosenOutputDeviceId');
    if (defaultOutputSource && this._validateDeviceId(defaultOutputSource)) {
      return defaultOutputSource;
    } else {
      // We don't have a valid saved device - use the first available
      let deviceId = this.getAvailableOutputSources()[0]?.deviceId;
      return deviceId;
    }
  }

  getAllDevices(): Promise<OpenTokDeviceList> {
    return this._enumerateDevices();
  }

  setAllDevices(aDeviceList): void {
    this._deviceList = aDeviceList;
  }

  getAvailableAudioSources(): OpenTokInputDevice[] {
    return this._deviceList?.inputs.filter(function (d) {
      return d.kind == 'audioinput';
    });
  }

  getAvailableVideoSources(): OpenTokInputDevice[] {
    return this._deviceList?.inputs.filter(function (d) {
      return d.kind == 'videoinput';
    });
  }

  getAvailableOutputSources(): OpenTokOutputDevice[] {
    return this._deviceList?.outputs;
  }

  setDefaultAudioDevice(deviceId: string): void {
    if (this._validateDeviceId(deviceId)) {
      localStorage.setItem('chosenAudioInputDeviceId', deviceId);
    }
  }

  setDefaultVideoDevice(deviceId: string): void {
    if (this._validateDeviceId(deviceId)) {
      localStorage.setItem('chosenVideoInputDeviceId', deviceId);
    }
  }

  setDefaultOutputDevice(deviceId: string): void {
    if (this._validateDeviceId(deviceId)) {
      localStorage.setItem('chosenOutputDeviceId', deviceId);
    }
  }

  switchToDevice(device: OpenTokInputDevice): void {
    let chosenDevice: OpenTokChosenDevices = {
      audioDeviceId: null,
      videoDeviceId: null,
      outputDeviceId: null,
    };
    if (device.kind == 'audioinput') {
      chosenDevice.audioDeviceId = device.deviceId;
    } else if (device.kind == 'videoinput') {
      chosenDevice.videoDeviceId = device.deviceId;
    } else {
      chosenDevice.outputDeviceId = device.deviceId;
    }
    App.INSTANCE.dispatch('inputdevice:switch', chosenDevice);
    App.INSTANCE.dispatch('inputdevice:toggle', {
      type: device.kind,
      toggle: true,
    });
  }

  muteAudio(aBoolean: boolean): void {
    if (aBoolean) {
      OpentokSession.INSTANCE.getStreamManager()
        .getSubscribers()
        .forEach((subscriber: OpenTokSubscriber) => {
          subscriber.setAudioVolume(0);
        });
    } else {
      OpentokSession.INSTANCE.getStreamManager()
        .getSubscribers()
        .forEach((subscriber: OpenTokSubscriber) => {
          subscriber.setAudioVolume(100);
        });
    }
  }

  private _runConnectivityTest(): Promise<void> {
    return new Promise((resolve, reject) => {
      App.INSTANCE.mApi.Request(
        `${CrowdHub_App.CONFIG.BASE_URL}custom_api/api_call_getCallTest.php`,
        {},
        successResponse => {
          if (successResponse.success == 'true') {
            var otNetworkTest = new OpenTokNetworkConnectivity.default(OT, {
              apiKey: successResponse.session.api_key,
              sessionId: successResponse.session.session,
              token: successResponse.session.o_token,
            });

            otNetworkTest
              .testConnectivity()
              .then(results => {
                console.info('OpenTok connectivity test results', JSON.stringify(results));
                // if (CrowdHub_App.CONFIG.TEST_AWS_PINGBACK) {
                //     results = this.checkNetworkStrength(results);
                // }
                if (CrowdHub_App.CONFIG.TEST_OT_QUALITY) {
                  this._runOtQualityTest(successResponse)
                    .then(results => {
                      results.success = true;
                      resolve(results);
                    })
                    .catch(error => {
                      console.error(error);
                      results.success = true;
                      resolve(results);
                    });
                } else {
                  if (results.failedTests?.length > 0) {
                    results.success = false;
                  } else {
                    results.success = true;
                  }
                  resolve(results);
                }
              })
              .catch(error => {
                console.error('OpenTok connectivity test error', JSON.stringify(error));
                resolve(error);
              });
          } else {
            App.INSTANCE.showTextMessage('Unable to reach server');
            reject(successResponse);
          }
        },
        error => {
          console.error(error);
        },
      );
    });
  }

  private _runOtQualityTest(sessionData): Promise<{}> {
    return new Promise((resolve, reject) => {
      var otNetworkTest = new OpenTokNetworkConnectivity.default(OT, {
        apiKey: sessionData.session.api_key,
        sessionId: sessionData.session.session,
        token: sessionData.session.o_token,
      });

      otNetworkTest
        .testQuality(function updateCallback(stats) {
          console.info('intermediate testQuality stats', JSON.stringify(stats));
        })
        .then(results => {
          // This function is called when the quality test is completed.
          console.info('OpenTok quality results', JSON.stringify(results));
          let publisherSettings = {
            audioSource: '',
            videoSource: '',
            frameRate: 0,
            resolution: '',
          };
          if (results.video.reason) {
            console.error('Video not supported:', results.video.reason);
            publisherSettings.videoSource = null; // audio-only
            resolve(results);
          } else {
            publisherSettings.frameRate = results.video.recommendedFrameRate;
            publisherSettings.resolution = results.video.recommendedResolution;
          }
          if (!results.audio.supported) {
            console.error('Audio not supported:', results.audio.reason);
            publisherSettings.audioSource = null;
            // video-only, but you probably don't want this -- notify the user?
            resolve(results);
          }
          if (!publisherSettings.videoSource && !publisherSettings.audioSource) {
            // ! TODO Do not publish. Notify the user.
          }
          resolve(results);
        })
        .catch(error => {
          console.error('OpenTok quality test error', JSON.stringify(error));
          switch (error.name) {
            case ErrorNames.UNSUPPORTED_BROWSER:
              // Display UI message about unsupported browser
              console.error('Unsupported browser');
              break;
            case ErrorNames.CONNECT_TO_SESSION_NETWORK_ERROR:
              // Display UI message about network error
              console.error('Network error');
              break;
            case ErrorNames.FAILED_TO_OBTAIN_MEDIA_DEVICES:
              // Display UI message about granting access to the microphone and camera
              console.error('Failed to obtain media devices');
              break;
            case ErrorNames.NO_AUDIO_CAPTURE_DEVICES:
              // Display UI message about no available microphone
              console.error('Failed to capture microphone');
              break;
            case ErrorNames.NO_VIDEO_CAPTURE_DEVICES:
              // Display UI message about no available camera
              console.error('Failed to capture camera');
              break;
            default:
              console.error('Unknown error: ' + error);
          }
          resolve(error);
        });
    });
  }

  private _validateDeviceId(deviceId: string): boolean {
    let inputItem = this.getDeviceList()
      .inputs.map(function (d) {
        return d.deviceId;
      })
      .includes(deviceId);
    if (inputItem) {
      return true;
    }
    let outputItem = this.getDeviceList()
      .outputs.map(function (d) {
        return d.deviceId;
      })
      .includes(deviceId);
    return outputItem;
  }

  private deviceChangeDetected(e: Event): void {
    // For some reason the device change event fires twice for me...this is to prevent issues with that - Matt
    if (e.timeStamp - this.deviceChangeTimestamp < 50) {
      return;
    }

    this.deviceChangeTimestamp = e.timeStamp;
    let oldDevices = this._deviceList;

    this.getAllDevices().then(newDevices => {
      // Check if current device was removed?
      let removedDevices = this.diffDevices(oldDevices, newDevices);

      if (removedDevices.length > 0) {
        // on disconnect - disable input for that device
        console.log('Removed device', removedDevices);
        removedDevices.forEach(removedDevice => {
          App.INSTANCE.dispatch('inputdevice:toggle', {
            type: removedDevice.kind,
            toggle: false,
          });
        });
      } else {
        let addedDevices = this.diffDevices(newDevices, oldDevices);

        console.info('Added device', addedDevices);

        let newAudioDevices = addedDevices.filter(device => {
          return device.kind == 'audioinput';
        });
        let newVideoDevices = addedDevices.filter(device => {
          return device.kind == 'videoinput';
        });
        let newOutputDevices = addedDevices.filter(device => {
          return device.kind == 'audiooutput';
        });

        if (newAudioDevices.length > 0) {
          this.notifyUserOfNewDevice(newAudioDevices[0]);
        }

        if (newVideoDevices.length > 0) {
          this.notifyUserOfNewDevice(newVideoDevices[0]);
        }

        if (newOutputDevices.length > 0) {
          this.notifyUserOfNewDevice(newOutputDevices[0]);
        }
      }
      this._deviceList = newDevices;
    });
  }

  // Returns devices that are in list1 that aren't in list2
  private diffDevices(list1: OpenTokDeviceList, list2: OpenTokDeviceList): OpenTokDevice[] {
    var diffInputs: OpenTokDevice[] = [];

    list1.inputs.forEach(d1 => {
      var hasDevice = false;
      list2.inputs.forEach(d2 => {
        if (d1.deviceId == d2.deviceId && d1.kind == d2.kind) {
          hasDevice = true;
        }
      });
      if (!hasDevice) {
        diffInputs.push(d1);
      }
    });

    list1.outputs.forEach(d1 => {
      var hasDevice = false;
      list2.outputs.forEach(d2 => {
        if (d1.deviceId == d2.deviceId && d1.kind == d2.kind) {
          hasDevice = true;
        }
      });
      if (!hasDevice) {
        diffInputs.push(d1);
      }
    });

    return diffInputs;
  }

  private notifyUserOfNewDevice(device: OpenTokDevice): void {
    let switchButton = new BaseCSSButton();

    let markup = HGEN.tree({
      style: 'new-device-detected',
      children: [
        {
          interior: CrowdHub_App.LANG.NEW_DEVICE_DETECTED.replace('*|DEVICE|*', device.label),
        },
        { style: 'new-device-button-wrapper' },
      ],
    });

    let type = '';
    switch (device.kind) {
      case 'audioinput':
        type = 'Audio';
        break;
      case 'videoinput':
        type = 'Video';
        break;
      case 'audiooutput':
        type = 'Output';
        break;
    }

    let toastr = new ToastrMessages(
      markup,
      CrowdHub_App.LANG.NEW_DEVICE_DETECTED_TITLE.replace('*|DEVICE_TYPE|*', type),
      'info',
      {
        positionClass: 'toast-bottom-full-width',
        timeOut: 7000,
        extendedTimeOut: 3000,
      },
    );

    toastr.showToastr();

    switchButton.initialize({
      id: 'switch-device-btn',
      textStyle: '',
      fa_icon: '',
      buttonStyle: 'toastr_text_button',
      text: CrowdHub_App.LANG.NEW_DEVICE_DETECTED_BTN,
      click: () => {
        this.switchToDevice(device);
        toastr.remove();
      },
    });

    $('.new-device-button-wrapper').html(switchButton.mDiv);
  }

  /** //? See https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices */
  private _enumerateDevices(): Promise<OpenTokDeviceList> {
    return new Promise<OpenTokDeviceList>((resolve, reject) => {
      navigator.mediaDevices
        .enumerateDevices()
        .then(devices => {
          let allInputDevices = devices.filter(d => {
            return d.kind == 'videoinput' || d.kind == 'audioinput';
          });
          let allOutputDevices = devices.filter(d => {
            return d.kind == 'audiooutput';
          });
          var inputCloneTarget;
          allInputDevices.forEach(function (device, index) {
            if (device.deviceId == 'default') {
              inputCloneTarget = index;
            }
          });
          var outputCloneTarget;
          allOutputDevices.forEach(function (device, index) {
            if (device.deviceId == 'default') {
              outputCloneTarget = index;
            }
          });
          if (inputCloneTarget && inputCloneTarget !== -1) {
            allInputDevices[inputCloneTarget] = JSON.parse(JSON.stringify(allInputDevices[inputCloneTarget]));
            allInputDevices[inputCloneTarget].label = CrowdHub_App.LANG.DEFAULT_DEVICE;
          }
          if (outputCloneTarget && outputCloneTarget !== -1) {
            allOutputDevices[outputCloneTarget] = JSON.parse(JSON.stringify(allOutputDevices[outputCloneTarget]));
            allOutputDevices[outputCloneTarget].label = CrowdHub_App.LANG.DEFAULT_DEVICE;
          }
          let aDeviceList = {
            inputs: allInputDevices,
            outputs: allOutputDevices,
          };
          resolve(aDeviceList);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  private _getNavigatorMedia(): void {
    navigator.mediaDevices
      .getUserMedia({ audio: true, video: true })
      .then(this._gotStream.bind(this))
      .catch(error => {
        console.error(error);
      });
  }

  private _gotStream(stream): void {
    window.stream = stream; // make the stream available
    this._windowStream = stream;
    // have to call stop on each MediaStreamTrack to turn off any found devices
    stream.getTracks().forEach(track => {
      track.stop();
    });
    this.getAllDevices().then(devicesList => {
      this._deviceList = devicesList;
    });
  }
}
